/** Derived unit statistics. Base values come from hero/mob defs; modifiers are summed. */
export interface Stats {
  maxHp: number;
  hpRegen: number; // hp per second
  armor: number; // flat; reduction = armor / (armor + 60)
  damage: number; // multiplier on all damage dealt
  attackSpeed: number; // multiplier on weapon fire rate
  moveSpeed: number; // px per second
  critChance: number; // 0..1
  critMult: number;
  cooldown: number; // multiplier on skill cooldowns (lower is better)
  area: number; // multiplier on radii
  projSpeed: number;
  projCount: number; // additional projectiles
  pierce: number; // additional pierce
  range: number; // multiplier
  lifesteal: number; // fraction of damage dealt returned as hp
  thorns: number; // fraction of damage taken reflected
  summonCount: number; // additional summons
  summonPower: number; // multiplier on summon hp and damage
  auraPower: number;
  auraRadius: number;
  healPower: number;
  xpGain: number;
  luck: number; // affects drop rarity
  dashCharges: number;
  dashCooldown: number; // multiplier
  statusPower: number; // multiplier on status damage and duration
  duration: number; // multiplier on skill effect durations
  pickupRadius: number;
}

export type StatKey = keyof Stats;

export interface Modifier {
  stat: StatKey;
  /** Flat addition applied before percentages. */
  add?: number;
  /** "Increased" percentage; all inc values on the same stat are summed (0.2 = +20%). */
  inc?: number;
  /** "More" multiplier; applied multiplicatively (1.5 = x1.5). */
  more?: number;
}

export const BASE_STATS: Stats = {
  maxHp: 100,
  hpRegen: 0.5,
  armor: 0,
  damage: 1,
  attackSpeed: 1,
  moveSpeed: 190,
  critChance: 0.05,
  critMult: 1.5,
  cooldown: 1,
  area: 1,
  projSpeed: 1,
  projCount: 0,
  pierce: 0,
  range: 1,
  lifesteal: 0,
  thorns: 0,
  summonCount: 0,
  summonPower: 1,
  auraPower: 1,
  auraRadius: 1,
  healPower: 1,
  xpGain: 1,
  luck: 0,
  dashCharges: 1,
  dashCooldown: 1,
  statusPower: 1,
  duration: 1,
  pickupRadius: 60,
};

/** Stats where the base is a multiplier and percentages scale it (vs. flat stats). */
const ADDITIVE_ONLY: Partial<Record<StatKey, true>> = {
  critChance: true,
  lifesteal: true,
  thorns: true,
  projCount: true,
  pierce: true,
  summonCount: true,
  dashCharges: true,
  luck: true,
};

export function applyModifiers(base: Stats, mods: readonly Modifier[]): Stats {
  const out: Stats = { ...base };
  const inc: Partial<Record<StatKey, number>> = {};
  const more: Partial<Record<StatKey, number>> = {};
  for (const m of mods) {
    if (m.add) out[m.stat] += m.add;
    if (m.inc) inc[m.stat] = (inc[m.stat] ?? 0) + m.inc;
    if (m.more) more[m.stat] = (more[m.stat] ?? 1) * m.more;
  }
  for (const k of Object.keys(out) as StatKey[]) {
    if (ADDITIVE_ONLY[k]) {
      if (more[k]) out[k] *= more[k]!;
      continue;
    }
    if (inc[k]) out[k] *= 1 + inc[k]!;
    if (more[k]) out[k] *= more[k]!;
  }
  out.critChance = Math.min(1, Math.max(0, out.critChance));
  out.cooldown = Math.max(0.3, out.cooldown);
  out.moveSpeed = Math.max(40, out.moveSpeed);
  out.maxHp = Math.max(1, Math.round(out.maxHp));
  out.armor = Math.max(0, out.armor);
  return out;
}

export function armorReduction(armor: number): number {
  return armor <= 0 ? 0 : armor / (armor + 60);
}

/** XP required to reach the next level from `level`. Tuned for ~45-60s per level. */
export function xpForLevel(level: number): number {
  return Math.round(20 + level * level * 6 + level * 14);
}

export const MAX_LEVEL = 20;
export const KEYSTONE_LEVELS = [5, 10, 15];
export const ASPECT_LEVEL = 3;

// ------------------------------------------------------------------ rank: the persistent track
// Level resets every realm and buys temporary power; rank persists on the hero and buys options.

export const RANK_MAX = 20;
/** XP from rank `r` to `r + 1`: the first realm reaches rank 5, about thirty realms reach 20. */
export function rankXpForRank(r: number): number {
  return Math.round(155 * (Math.pow(r + 1, 2.45) - Math.pow(r, 2.45)));
}
/** Rank points available at a rank; the start skill is free on top. */
export function rankPoints(rank: number): number {
  return Math.max(0, Math.min(RANK_MAX, rank));
}
export const RANK_COST = { skill: 1, aspect1: 2, aspect2: 3, trait: 1 } as const;
/** Rank xp awarded for slaying the realm boss. */
export const BOSS_RANK_XP = 1500;
/** Fame charged for a respec from this rank onward. */
export const RESPEC_FREE_BELOW_RANK = 10;
export const RESPEC_FAME = 200;
